import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useContext, useEffect, useCallback, useMemo, useRef, useState, } from 'preact/compat';
import { useFormContext, } from 'react-hook-form';
import { clsx } from 'clsx';
import StyledForm from '@bookingForm/styles/form';
import Overview from '@genericBookings/components/Overview';
import { ThemeContext } from 'styled-components';
import TermsCheckbox from '@genericBookings/components/TermsCheckbox/TermsCheckbox';
import { TermsWrapper } from '@bookingForm/styles/misc';
import TermsModal from '@widgets/shared/components/TermsModal';
import QuickPay from '@widgets/shared/components/QuickPay';
import { SubmitButton } from '@bookingForm/styles/buttons';
import VoucherMessage from '@bookingForm/components/VoucherMessage';
import useTermsClasses from '@genericBookings/hooks/useTermsClasses';
import { calculateTotalPrice } from '@genericBookings/calculatePrice';
import useGlobalEvent from 'beautiful-react-hooks/useGlobalEvent';
import useDidMount from 'beautiful-react-hooks/useDidMount';
import { useStepCompletion } from '@widgets/DayTickets/context/Steps';
import { getActivities, getSelectedActivities } from '@genericBookings/store/reducers/activities';
import { useAppDispatch, useAppSelector } from '@genericBookings/store/hooks';
import { useMessage } from '@genericBookings/context/Message';
import { useFieldsWrapper } from '@genericBookings/context/FieldsWrapper';
import { getQuickPayAppleState, getQuickPayGoogleState, setQuickPayState } from '@genericBookings/store/reducers/quickPay';
import { formCompleteEvent } from '@genericBookings/primaryActionEvents';
import { DummyLink } from '@genericBookings/components/SelectedOption/styles';
import { useCourse } from '@bookingForm/context/Course';
function SubmissionWrapper({ layout, options, children, productId, }) {
    const { getValues, setValue, watch, formState: { isValid, touchedFields }, } = useFormContext();
    const termsRef = useRef(null);
    const submitSectionRef = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);
    const handleOpen = useCallback(() => setModalOpen(true), [setModalOpen]);
    const handleClose = useCallback(() => setModalOpen(false), [setModalOpen]);
    const onGoogleAvailable = useGlobalEvent('google.available');
    const onAppleAvailable = useGlobalEvent('apple.available');
    const { hideQuickPayButtons, termsHeading, termsSubheading, termsContent, analyticsUrl, analyticsTitle, analyticsPath, submitButtonText, isBuilder, isMulti, requiresSignature, debug, } = useContext(options);
    const { isSubmitting, handleSubmitEvent, } = useFieldsWrapper();
    const dispatch = useAppDispatch();
    const activitiesSelector = isBuilder ? getSelectedActivities : getActivities;
    const selectedActivities = useAppSelector(activitiesSelector);
    const { handleClick: handleDisabledFormClick, termsClasses, } = useTermsClasses();
    const { isCompleted, isLastStep, } = useStepCompletion();
    const { isCourse } = useCourse();
    const termsAccepted = watch('acceptTerms');
    const { errors } = useMessage();
    const appleHidden = useAppSelector(getQuickPayAppleState);
    const googleHidden = useAppSelector(getQuickPayGoogleState);
    const hasIncompleteActivities = useMemo(() => selectedActivities.some((curr) => !curr.activeSession), [selectedActivities]);
    const activeSessionIds = useMemo(() => selectedActivities
        .map((activity) => activity.activeSession?.SessionId)
        .filter((sessionId) => sessionId !== undefined)
        .join(','), [selectedActivities]);
    const formValid = useMemo(() => isValid
        && (isCompleted || isLastStep)
        && !hasIncompleteActivities
        && errors.length === 0
        && !isSubmitting, [isValid, isCompleted, isLastStep, hasIncompleteActivities, errors.length, isSubmitting]);
    const formValidNoSubmitting = useMemo(() => isValid && (isCompleted || isLastStep) && !hasIncompleteActivities && errors.length === 0, [errors.length, hasIncompleteActivities, isCompleted, isLastStep, isValid]);
    const date = getValues('VisitDate');
    const activityToQuickPayProduct = useCallback((activity) => {
        const quickPayProduct = {
            type: 'product',
            id: activity.activity.id,
            SessionId: activity.activeSession?.SessionId ?? -1,
            VisitDate: date ?? '',
        };
        // doing this in case they're set differently per activity as they sometimes are...
        if (activity.visitors) {
            Object.keys(activity.visitors)
                .forEach((_, idx) => {
                const visitorsKey = `Visitors${idx}`;
                // @ts-ignore ignoring as TS is talking nonsense here...
                quickPayProduct[`Visitors${idx}`] = activity.visitors !== undefined
                    ? (activity.visitors[visitorsKey] ?? 0)
                    : 0;
            });
        }
        else {
            Array(9)
                .fill(0)
                .forEach((_, idx) => {
                quickPayProduct[`Visitors${idx}`] = getValues(`Visitors${idx}`);
            });
        }
        return quickPayProduct;
    }, [date, getValues]);
    const quickPayProducts = useMemo(() => {
        // unforunately I have commited myself to 3 diffe nt types of this in the early iterations of booking/daytickets
        // I can't rework without a bunch of work Steve's side so having to live with this wonky solution...
        if (!isBuilder && !isMulti && selectedActivities.length > 0) {
            // this decision might bite me in the ass in the future but for now this is the only way of doing it without some
            // major re-engineering of a lot of files.
            return activityToQuickPayProduct(selectedActivities[0]);
        }
        if (!isBuilder && isMulti) {
            const quickPayProduct = {
                type: 'dayTicket',
                id: productId,
                VisitDate: date ?? '',
                activities: selectedActivities.map((activity) => ({
                    SessionId: activity.activeSession?.SessionId ?? -1,
                    ActivityId: activity.activity.id,
                })),
            };
            quickPayProduct.type = 'dayTicket';
            quickPayProduct.id = productId;
            quickPayProduct.VisitDate = date ?? '';
            Array(9)
                .fill(0)
                .forEach((_, idx) => {
                quickPayProduct[`Visitors${idx}`] = getValues(`Visitors${idx}`);
            });
            return quickPayProduct;
        }
        if (isBuilder && isMulti) {
            return selectedActivities.map((curr) => activityToQuickPayProduct(curr));
        }
        return undefined;
    }, [isBuilder, isMulti, selectedActivities, activityToQuickPayProduct, productId, date, getValues]);
    const onShakeTerms = useCallback(() => {
        handleDisabledFormClick(true);
    }, [handleDisabledFormClick]);
    const submitButtonClasses = clsx('btn', layout === 'winter-wonderland' ? 'btn--secondary' : 'btn--primary', !formValid && 'btn--disabled');
    const { showOverviewAttendees } = useContext(ThemeContext);
    const handleDidMount = useCallback(() => {
        // if we should render the quick pay buttons, but they're not ready to be displayed e.g. sessions not selected, send event
        // and then wait for us to receive google.available / apple.available in response.
        if (!hideQuickPayButtons) {
            window.dispatchEvent(new CustomEvent('PaymentButtonsLoaded'));
        }
    }, [hideQuickPayButtons]);
    const showQuickPay = useMemo(() => !(appleHidden && googleHidden)
        && !hideQuickPayButtons
        && quickPayProducts !== undefined
        && calculateTotalPrice(selectedActivities) !== 0
        && formValid
        && !isCourse, [
        appleHidden,
        googleHidden,
        hideQuickPayButtons,
        formValid,
        quickPayProducts,
        selectedActivities,
        isCourse,
    ]);
    const handleQuickPayDidFail = useCallback((status, os) => {
        dispatch(setQuickPayState(os, status));
    }, [dispatch]);
    const handleAcceptedPaymentForms = useCallback(() => {
        setValue('acceptTerms', ((appleHidden && googleHidden) || hideQuickPayButtons), { shouldValidate: true });
    }, [
        appleHidden,
        googleHidden,
        hideQuickPayButtons,
        setValue,
    ]);
    const handleSelectedActivitiesChange = useCallback(() => {
        if ((!showQuickPay && selectedActivities.length && calculateTotalPrice(selectedActivities) === 0)) {
            setValue('acceptTerms', true, { shouldValidate: true });
        }
    }, [showQuickPay, selectedActivities, setValue]);
    const handleTermsChanged = useCallback(() => {
        if (touchedFields && touchedFields.acceptTerms
            && termsAccepted
            && requiresSignature
            && (!appleHidden || !googleHidden)
            && !hasIncompleteActivities) {
            setModalOpen(termsAccepted);
        }
    }, [
        touchedFields,
        termsAccepted,
        requiresSignature,
        appleHidden,
        googleHidden,
        hasIncompleteActivities,
    ]);
    const handleTermsCancelled = useCallback(() => {
        setValue('acceptTerms', false, { shouldValidate: true });
        setModalOpen(false);
    }, [setValue]);
    useDidMount(handleDidMount);
    onAppleAvailable(() => handleQuickPayDidFail(false, 'apple'));
    onGoogleAvailable(() => handleQuickPayDidFail(false, 'google'));
    useEffect(handleSelectedActivitiesChange, [handleSelectedActivitiesChange, selectedActivities]);
    useEffect(() => {
        if (layout !== 'winter-wonderland' || hasIncompleteActivities || !activeSessionIds) {
            return undefined;
        }
        const timeoutId = window.setTimeout(() => {
            submitSectionRef.current?.scrollIntoView({
                block: 'start',
                behavior: 'smooth',
            });
        }, 50);
        return () => window.clearTimeout(timeoutId);
    }, [layout, hasIncompleteActivities, activeSessionIds]);
    useEffect(() => {
        if (formValidNoSubmitting) {
            formCompleteEvent(isSubmitting, () => handleSubmitEvent());
        }
    }, [formValidNoSubmitting, handleSubmitEvent, isSubmitting]);
    useEffect(handleAcceptedPaymentForms, [appleHidden, googleHidden, hideQuickPayButtons, handleAcceptedPaymentForms]);
    useEffect(handleTermsChanged, [termsAccepted, handleTermsChanged]);
    // {debug && (
    //   <pre style={{ color: '#fff' }}>
    //     {
    //       JSON.stringify({
    //         isValid,
    //         complete: (isLastStep || isCompleted),
    //         hasIncompleteActivities,
    //         errors: errors.length,
    //         isSubmitting,
    //         showQuickPay,
    //       }, null, 2)
    //     }
    //   </pre>
    // )}
    return (_jsxs(_Fragment, { children: [_jsxs(StyledForm.Submit, { ref: submitSectionRef, children: [children, _jsx(Overview, { showAttendees: showOverviewAttendees ?? false, layout: layout ?? 'rework', attendeesString: "", options: options }), showQuickPay && (_jsx(TermsWrapper, { className: termsClasses, ref: termsRef, children: _jsx(TermsCheckbox, { name: "acceptTerms", onLinkClicked: handleOpen }) })), showQuickPay && quickPayProducts !== undefined && (_jsx(QuickPay, { analyticsTitle: analyticsTitle, analyticsPath: analyticsPath, analyticsUrl: analyticsUrl, products: quickPayProducts, termsAccepted: getValues('acceptTerms'), title: "Quick, easy and secure:", onClickedWhenDisabled: onShakeTerms, handleGoogleDidFail: (status) => handleQuickPayDidFail(status, 'google'), handleAppleDidFail: (status) => handleQuickPayDidFail(status, 'apple'), appleHidden: appleHidden, googleHidden: googleHidden, disabled: !formValid, paymentButtonsReady: true, darkButtons: layout === 'winter-wonderland' })), _jsx(SubmitButton, { type: "submit", className: submitButtonClasses, style: { marginTop: !showQuickPay ? '1rem' : '2rem' }, onClick: (e) => {
                            if (!formValid) {
                                e.preventDefault();
                                handleDisabledFormClick(true);
                            }
                        }, children: submitButtonText }), _jsx(VoucherMessage, { options: options, showQuickPay: showQuickPay })] }), (modalOpen && termsContent) && (_jsx(TermsModal, { name: "terms", termsHeading: termsHeading ?? '', termsSubheading: termsSubheading ?? '', termsContent: termsContent ?? '', onConfirm: handleClose, onCancel: handleTermsCancelled, termsShowCheckbox: false, showSignatureField: requiresSignature })), (debug && !hideQuickPayButtons && quickPayProducts !== undefined) && (_jsxs("div", { style: { width: '100%' }, className: "rfx-grid", children: [appleHidden && (_jsx("div", { className: "rfx-col-md-4 rfx-col-6", children: _jsx(DummyLink, { onClick: () => {
                                window.dispatchEvent(new CustomEvent('apple.available'));
                            }, children: "Show Apple" }) })), googleHidden && (_jsx("div", { className: "rfx-col-md-4 rfx-col-6", children: _jsx(DummyLink, { onClick: () => {
                                window.dispatchEvent(new CustomEvent('google.available'));
                            }, children: "Show Google" }) })), _jsx("div", { className: "rfx-col-md-4 rfx-col-6", children: _jsx(DummyLink, { onClick: () => {
                                window.dispatchEvent(new CustomEvent('reservation.failed', {
                                    detail: 'Testing Reservation Failure...',
                                }));
                            }, children: "Reservation Failed" }) }), _jsx("div", { className: "rfx-col-md-4 rfx-col-6", children: _jsx(DummyLink, { onClick: () => {
                                window.dispatchEvent(new CustomEvent('payment.success', {
                                    detail: {
                                        successUrl: '#',
                                    },
                                }));
                            }, children: "Payment Success" }) }), _jsx("div", { className: "rfx-col-md-4 rfx-col-6", children: _jsx(DummyLink, { onClick: () => {
                                window.dispatchEvent(new CustomEvent('payment.error', {
                                    detail: {
                                        errorMessage: 'Payment Error Test',
                                    },
                                }));
                            }, children: "Payment Error" }) })] }))] }));
}
SubmissionWrapper.defaultProps = {
    layout: 'rework',
};
export default SubmissionWrapper;
